import React, { useEffect, useState, useMemo } from "react";
import { api, formatMinutes } from "../lib/api";
import { Button } from "../components/ui/button";
import { Plus, ShieldCheck, Sparkles } from "lucide-react";
import AddStudyModal from "../components/AddStudyModal";
import StreakCalendar from "../components/StreakCalendar";
import { Link } from "react-router-dom";

const QUOTES = [
  '"A vida é 10% o que acontece com você e 90% como você reage." — Charles R. Swindoll',
  '"O sucesso é a soma de pequenos esforços repetidos dia após dia." — Robert Collier',
  '"Disciplina é a ponte entre metas e realizações." — Jim Rohn',
  '"Não conte os dias, faça os dias contarem." — Muhammad Ali',
  '"A persistência é o caminho do êxito." — Charles Chaplin',
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [weeklyGoals, setWeeklyGoals] = useState({ hours: 7, questions: 400 });

  const quote = useMemo(() => QUOTES[Math.floor(Math.random() * QUOTES.length)], []);

  const load = async () => {
    const [s, d] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/disciplines"),
    ]);
    setStats(s.data);
    setDisciplines(d.data);
  };

  useEffect(() => { load(); }, []);

  // load weekly goals from localStorage
  useEffect(() => {
    const g = localStorage.getItem("weekly_goals");
    if (g) setWeeklyGoals(JSON.parse(g));
  }, []);

  if (!stats) return <div className="animate-pulse text-muted-foreground">Carregando...</div>;

  const perf = stats.performance;
  const progress = stats.progress;

  // weekly minutes / questions - filter last 7 days from studied dates + per_discipline is aggregate lifetime
  // For weekly, we need per-day data; use studied_dates count as proxy - but we can use aggregate for now
  const weeklyMin = 0; // TODO improve when sessions endpoint used

  return (
    <div className="space-y-6 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-extrabold text-foreground">Home</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => setModalOpen(true)} className="bg-tiffany-500 hover:bg-tiffany-600 text-white font-semibold">
            <Plus size={16} className="mr-1" /> Adicionar Estudo
          </Button>
          <div className="px-4 h-10 rounded-md border-2 border-tiffany-500 text-tiffany-600 dark:text-tiffany-400 font-semibold flex items-center gap-2">
            <ShieldCheck size={16} /> Plano POLÍCIA
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="TEMPO DE ESTUDO" bigValue={formatMinutes(stats.total_minutes)} />
        <StatCard
          title="DESEMPENHO"
          lines={[
            { text: `${stats.total_correct} Acertos`, color: "text-green-600" },
            { text: `${stats.total_wrong} Erros`, color: "text-red-500" },
          ]}
          bigValue={`${perf}%`}
        />
        <StatCard
          title="PROGRESSO NO EDITAL"
          lines={[
            { text: `${stats.completed_topics} Tópicos Concluídos`, color: "text-green-600" },
            { text: `${stats.pending_topics} Tópicos Pendentes`, color: "text-orange-500" },
          ]}
          bigValue={`${progress}%`}
        />
        <div className="rounded-2xl bg-card border border-border p-5 flex items-center justify-center text-center">
          <div>
            <Sparkles size={18} className="mx-auto mb-2 text-tiffany-500" />
            <p className="italic text-sm text-muted-foreground leading-relaxed">{quote}</p>
          </div>
        </div>
      </div>

      {/* Streak Calendar */}
      <StreakCalendar studiedDates={stats.studied_dates} />

      {/* Two-column: Painel + Right Column */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-2xl bg-card border border-border p-5 md:p-6">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-4">Painel</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="text-left py-2 font-semibold">Disciplinas</th>
                  <th className="text-center py-2 font-semibold">Tempo</th>
                  <th className="text-center py-2 font-semibold"><span className="text-green-600">✓</span></th>
                  <th className="text-center py-2 font-semibold"><span className="text-red-500">✗</span></th>
                  <th className="text-center py-2 font-semibold">Q</th>
                  <th className="text-center py-2 font-semibold">%</th>
                </tr>
              </thead>
              <tbody>
                {stats.per_discipline.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">
                    Nenhuma disciplina ainda. <Link className="text-tiffany-600 underline" to="/disciplinas">Adicionar disciplina</Link>
                  </td></tr>
                )}
                {stats.per_discipline.map((d) => (
                  <tr key={d.id} className="border-b border-border/60 hover:bg-muted/40">
                    <td className="py-2">
                      <Link to="/disciplinas" className="font-semibold" style={{ color: d.color }}>{d.name}</Link>
                    </td>
                    <td className="text-center">{formatMinutes(d.minutes)}</td>
                    <td className="text-center text-green-600 font-semibold">{d.correct}</td>
                    <td className="text-center text-red-500 font-semibold">{d.wrong}</td>
                    <td className="text-center font-semibold">{d.questions}</td>
                    <td className="text-center">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                        d.performance >= 70 ? "bg-green-100 text-green-700" :
                        d.performance >= 50 ? "bg-yellow-100 text-yellow-700" :
                        "bg-red-100 text-red-700"
                      }`}>{d.performance}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <ExamDateCard />
          <WeeklyGoalsCard goals={weeklyGoals} setGoals={setWeeklyGoals} totalMin={stats.total_minutes} totalQ={stats.total_questions} />
        </div>
      </div>

      <AddStudyModal open={modalOpen} onOpenChange={setModalOpen} disciplines={disciplines} onCreated={load} />
    </div>
  );
}

function StatCard({ title, bigValue, lines }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5 flex flex-col justify-between min-h-[130px]">
      <h4 className="font-display font-bold text-xs uppercase tracking-wider text-muted-foreground">{title}</h4>
      {lines && (
        <div className="mt-2 space-y-0.5">
          {lines.map((l, i) => (
            <div key={i} className={`text-sm font-semibold ${l.color}`}>{l.text}</div>
          ))}
        </div>
      )}
      <div className="text-right font-display font-extrabold text-3xl text-foreground">{bigValue}</div>
    </div>
  );
}

function ExamDateCard() {
  const [exam, setExam] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", date: "" });

  const load = async () => {
    const r = await api.get("/exam-date");
    if (r.data && r.data.date) setExam(r.data);
  };
  React.useEffect(() => { load(); }, []);

  const daysLeft = exam ? Math.max(0, Math.ceil((new Date(exam.date) - new Date()) / (1000 * 60 * 60 * 24))) : null;

  const save = async () => {
    if (!form.name || !form.date) return;
    await api.post("/exam-date", form);
    setExam(form);
    setEditing(false);
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-5">
      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">Data da Prova</h3>
      {editing ? (
        <div className="space-y-2">
          <input
            className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
            placeholder="Nome da prova"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            type="date"
            className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} className="bg-tiffany-500 hover:bg-tiffany-600 text-white">Salvar</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
          </div>
        </div>
      ) : exam ? (
        <div>
          <div className="font-display font-extrabold text-2xl text-tiffany-600 dark:text-tiffany-400">{daysLeft} dias</div>
          <div className="text-sm text-muted-foreground">{exam.name}</div>
          <button className="text-xs text-tiffany-600 hover:underline mt-2" onClick={() => { setForm(exam); setEditing(true); }}>Editar</button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-2">Acompanhe aqui quantos dias faltam para sua prova!</p>
          <button className="text-tiffany-600 font-semibold underline" onClick={() => setEditing(true)}>Criar Prova</button>
        </div>
      )}
    </div>
  );
}

function WeeklyGoalsCard({ goals, setGoals, totalMin, totalQ }) {
  const save = (g) => {
    setGoals(g);
    localStorage.setItem("weekly_goals", JSON.stringify(g));
  };
  const hoursDone = Math.min(1, totalMin / 60 / goals.hours);
  const qDone = Math.min(1, totalQ / goals.questions);
  return (
    <div className="rounded-2xl bg-card border border-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">Metas de Estudo Semanal</h3>
      </div>
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-semibold">{formatMinutes(totalMin)}/{goals.hours}h00min</span>
            <span className="text-muted-foreground">Horas de Estudo</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-tiffany-500 transition-all" style={{ width: `${hoursDone * 100}%` }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-semibold">{totalQ}/{goals.questions}</span>
            <span className="text-muted-foreground">Questões</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-tiffany-500 transition-all" style={{ width: `${qDone * 100}%` }} />
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <input
          type="number"
          min="1"
          className="w-16 h-8 px-2 text-xs rounded border border-input bg-background"
          value={goals.hours}
          onChange={(e) => save({ ...goals, hours: Number(e.target.value) })}
        />
        <input
          type="number"
          min="1"
          className="w-20 h-8 px-2 text-xs rounded border border-input bg-background"
          value={goals.questions}
          onChange={(e) => save({ ...goals, questions: Number(e.target.value) })}
        />
        <span className="text-xs text-muted-foreground self-center">Ajustar metas</span>
      </div>
    </div>
  );
}
